import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

export const productTypeVariantDetailsSearchSchema = z.object({
  keyword: z.string().optional(),
  productTypeVariant: z.array(z.string()).optional(),
  ...datatableSearchSchema.shape,
})

export type ProductTypeVariantDetailsSearch = z.infer<typeof productTypeVariantDetailsSearchSchema>

export type ProductTypeVariants = Awaited<ReturnType<typeof getProductTypeVariants>>

async function getProductTypeVariants(
  search: ProductTypeVariantDetailsSearch = {},
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['product-type-variants'].$get(
    { query: stringifyValues(search) },
    { init: { signal } },
  )
  return await res.json()
}

export function useProductTypeVariants(queries: ProductTypeVariantDetailsSearch = {}) {
  return useQuery({
    queryKey: ['productTypeVariants', queries],
    queryFn: ({ signal }) => getProductTypeVariants(queries, signal),
  })
}

async function getDistinctProductTypeVariants(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['product-type-variants'].variants.$get({}, { init: { signal } })
  return await res.json()
}

export function useDistinctProductTypeVariants() {
  return useQuery({
    queryKey: ['distinctProductTypeVariants'],
    queryFn: ({ signal }) => getDistinctProductTypeVariants(signal),
  })
}

async function getProductTypeVariantSummary(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['product-type-variants'].summary.$get({}, { init: { signal } })
  return await res.json()
}

export const productTypeVariantSummaryQueryOptions = {
  queryKey: ['productTypeVariantSummary'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getProductTypeVariantSummary(signal),
}

export function useProductTypeVariantSummary() {
  return useQuery(productTypeVariantSummaryQueryOptions)
}

async function getProductTypeVariantsForExport(
  queries: ProductTypeVariantDetailsSearch = {},
  signal?: AbortSignal,
) {
  const stringifiedQueries = stringifyValues(queries)
  const filteredQueries = Object.fromEntries(
    Object.entries(stringifiedQueries).filter(([, value]) => value !== undefined),
  )
  const queryParams = new URLSearchParams(filteredQueries).toString()
  return fetchCsvExport(`/crt/product-type-variants/export?${queryParams}`, signal)
}

export const productTypeVariantDetailsExportQueryOptions = (
  queries: ProductTypeVariantDetailsSearch = {},
) => ({
  queryKey: ['productTypeVariantDetailsExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    getProductTypeVariantsForExport(queries, signal),
})

async function getProductTypeVariantsForRawExport(signal?: AbortSignal) {
  return fetchCsvExport('/crt/product-type-variants/raw-export', signal)
}

export const productTypeVariantDetailsRawExportQueryOptions = {
  queryKey: ['productTypeVariantDetailsRawExport'] as const,
  queryFn: ({ signal }: { signal: AbortSignal }) => getProductTypeVariantsForRawExport(signal),
}
